import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { EssayCard } from '@/components/EssayCard';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { toast } from 'sonner';
import { BookOpen } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function EssaysPage() {
  const [essays, setEssays] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  usePageTitle();

  useEffect(() => {
    api.get('/essays?limit=50').then(res => {
      setEssays(res.data.essays || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await api.post('/newsletter', { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe.');
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <section className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12 lg:gap-16">
            {/* Essays list */}
            <div>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              >
                <motion.p variants={fadeUp} className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
                  Writing Portal
                </motion.p>
                <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-6">
                  Essays
                </motion.h1>
                <motion.p variants={fadeUp} className="font-['Inter'] text-base leading-[1.7] text-[hsl(var(--muted-foreground))] mb-10 max-w-xl">
                  This is a living body of work. Each essay is a structured exploration of the patterns that shape consciousness, identity, and power.
                </motion.p>
              </motion.div>

              {loading ? (
                <div className="flex justify-center py-20"><ConcentricRings size={60} opacity={0.3} /></div>
              ) : essays.length === 0 ? (
                <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] py-20 text-center">No essays published yet.</p>
              ) : (
                <div className="space-y-0">
                  {essays.map((essay, i) => (
                    <motion.div
                      key={essay._id || essay.slug}
                      initial="hidden"
                      animate="visible"
                      custom={i}
                      variants={fadeUp}
                    >
                      <EssayCard essay={essay} />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-8">
                {/* Reading Room card */}
                <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="h-4 w-4 text-[hsl(var(--gold))]" />
                    <h4 className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))]">
                      The Reading Room
                    </h4>
                  </div>
                  <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))] mb-4">
                    These essays are not content. They are a structured practice of articulating what most leave in the peripheral vision.
                  </p>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))]">
                    {essays.length} essay{essays.length !== 1 ? 's' : ''} published
                  </p>
                </div>

                {/* Newsletter */}
                <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6">
                  <h4 className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
                    Join the Observatory
                  </h4>
                  <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))] mb-4">
                    Receive new essays and structured transmissions.
                  </p>
                  <form onSubmit={handleNewsletter} className="space-y-3">
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Your email"
                      className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                      data-testid="newsletter-email-input"
                      required
                    />
                    <Button
                      type="submit"
                      className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium rounded-lg hover:brightness-[1.03]"
                      data-testid="newsletter-submit-button"
                    >
                      Join the Observatory
                    </Button>
                  </form>
                </div>

                {/* Substack link */}
                <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6">
                  <h4 className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-3">
                    Also on Substack
                  </h4>
                  <a
                    href="https://substack.com/@emmakinglund"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
                  >
                    Visit our Substack profile
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
